/**
 * Shared types for the web push notifications feature.
 *
 * Keep this file dependency-free so it can be imported anywhere — including
 * test utilities — without pulling Firebase / React into the bundle graph.
 */

/** Browser-level capability state — exposed to UI for graceful degradation. */
export type PushSupport =
  | 'supported'
  | 'unsupported-browser' // missing Notification, ServiceWorker or PushManager
  | 'unsupported-context'; // not HTTPS / not a secure context

/** Mirrors `Notification.permission` plus our own `unsupported` sentinel. */
export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

/** Normalised foreground / background-click message dispatched to the page. */
export interface IncomingPushMessage {
  title: string;
  body: string;
  /** Click target if the user taps the notification. */
  url?: string;
  /** Free-form data block the backend put in `data`. */
  data: Record<string, string>;
  /** Stable id used to collapse repeats. */
  tag?: string;
  receivedAt: number;
  /** Where the message arrived. */
  source: 'foreground' | 'background' | 'background-click';
}

/** Custom DOM event name. Subscribe via `window.addEventListener(...)`. */
export const PUSH_MESSAGE_EVENT = 'myu:push-message' as const;

/** localStorage keys — single source of truth. */
export const PUSH_STORAGE_KEYS = {
  /** Last token successfully sent to the backend. */
  lastRegisteredToken: 'myu.push.lastToken',
  /** ISO timestamp of the last successful register call. */
  lastRegisteredAt: 'myu.push.lastRegisteredAt',
  /** True if the user explicitly opted out (so we don't re-prompt). */
  optedOut: 'myu.push.optedOut',
  /** A per-install id, stable across tabs of the same browser profile. */
  installationId: 'myu.push.installationId',
} as const;
